import { action, observable, computed, reaction, observe } from "mobx";
import {
  CognitoUserPool,
  CognitoUserAttribute,
  AuthenticationDetails,
  CognitoUser,
} from "amazon-cognito-identity-js";
import browserLanguage from "in-browser-language";

import axios from "./axios";
import Movie from "./models/Movie";
import Session from "./models/Session";

const LANG = browserLanguage.pick(
  process.env.REACT_APP_LANGUAGES.split(","),
  "en"
);

const COGNITO_ENVS = {
  production: {
    UserPoolId: "us-west-2_dUHFTa9gn",
    ClientId: "2sjnoengk099177npcojnven46",
  },
  hbodev: {
    UserPoolId: "us-west-2_OqJOLJOG5",
    ClientId: "2gjlv90en3ioo2veh8q16c37ao",
  },
  development: {
    UserPoolId: "us-west-2_1P3xMOXoy",
    ClientId: "8sfi1v53od33r41n1ovevu53d",
  },
};

const userPool = new CognitoUserPool(
  COGNITO_ENVS[process.env.REACT_APP_COGNITO_ENV]
);

export default class Store {
  @observable isInitialized = false;

  language = LANG;
  axios;
  cognitoUser = null;
  refreshInterval = null;
  // unix timestamp
  accessExpiration = 0;
  username;
  lang = LANG;
  observers = [];
  @observable accessToken = "";
  @observable facebookId = null;
  @observable movie;
  @observable session;
  @observable profile;
  @observable disableMapAnimation = false;

  @computed
  get isAuthenticated() {
    return !!(this.movie && this.session && this.profile && this.accessToken);
  }

  @computed
  get isFacebook() {
    return !!this.facebookId;
  }

  @computed
  get avatarUrl() {
    return this.isFacebook
      ? `//graph.facebook.com/${this.facebookId}/picture?type=large`
      : null;
  }

  constructor() {
    this.axios = axios(this);

    reaction(
      () => this.accessToken,
      token => {
        this.axios.defaults.headers.common["Authorization"] = token
          ? `Bearer ${token}`
          : "";
      }
    );
  }

  identify = () => {
    window.analytics.identify(this.profile._id, {
      email: this.username,
    });

    return Promise.resolve();
  };

  @action
  startApp = () => {
    /*
    return this.attemptAuthenticate()
      .then(() => {
        this.isInitialized = true;
        return Promise.resolve();
      })
      .catch(err => {
        window.analytics.track(`Web "Install"`);
        this.isInitialized = true;
        return Promise.resolve();
      }); */
  };

  @action
  attemptAuthenticate = () => {
    return new Promise((resolve, reject) => {
      if (window.FB) {
        window.FB.getLoginStatus(data => {
          if (data.status === "connected") {
            return resolve(this.handleFacebookLogin(data));
          } else {
            if (data.status === "not_authorized") {
              this.facebookNotAuthorized = true;
            }

            reject();
          }
        });
      } else {
        reject();
      }
    })
      .then(() => this.fetchInitialData())
      .catch(() => {
        this.cognitoUser = userPool.getCurrentUser();

        if (!this.cognitoUser) {
          return Promise.reject();
        }

        return new Promise((resolve, reject) => {
          this.cognitoUser.getSession((err, session) => {
            if (err || !session.isValid()) {
              this.logout();
              reject(err);
            } else {
              this.username = this.cognitoUser.getUsername();
              resolve(this.initializeCognitoSession(session));
            }
          });
        });
      });
  };

  @action
  initializeCognitoSession = (session, fetchData = true) => {
    const accessToken = session.getAccessToken();
    this.accessToken = accessToken.getJwtToken();

    this.refreshToken = session.getRefreshToken();
    this.accessExpiration = accessToken.getExpiration() * 1000;

    this.refreshSession(true);

    return fetchData
      ? this.fetchInitialData().then(this.identify)
      : Promise.resolve();
  };

  @action
  refreshSession = (initialize = false) => {
    if (initialize) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = setInterval(this.refreshSession, 60 * 1000);
    }

    // try 5 minutes before expiration
    const OFFSET = 5 * 60 * 1000;

    if (Date.now() >= this.accessExpiration - OFFSET) {
      return new Promise((resolve, reject) => {
        if (this.cognitoUser) {
          this.cognitoUser.refreshSession(this.refreshToken, (err, session) => {
            if (err) {
              this.accessToken = null;
              this.cognitoUser = null;
              this.logout();
              reject(err);
            } else {
              this.initializeCognitoSession(session, false);

              const accessToken = session.getAccessToken();
              return resolve(accessToken.getJwtToken());
            }
          });
        } else {
          window.FB.getLoginStatus(data => {
            if (data.status === "connected") {
              this.handleFacebookLogin(data).then(() => {
                resolve(data.authResponse.accessToken);
              });
            } else if (data.status === "not_authorized") {
              this.logout();
              reject();
            }
          }, true);
        }
      });
    }

    return Promise.resolve(this.accessToken);
  };

  @action
  fetchInitialData = () => {
    return this.fetchConfig()
      .then(() => this.fetchProfile())
      .then(() => {
        return this.fetchMovie();
      });
  };

  @action
  fetchConfig = () => {
    return this.axios
      .post("config", {
        clientPlatform: "web",
        clientVersion: "0.0.1",
        clientBuild: "0.0.1",
        deviceID: navigator.userAgent,
      })
      .then(({ data: config }) => {
        // https://github.com/mindsight/node-mongo-server/blob/master/docs/api.md#config
        this.config = {
          assetPath: "https://assets.watchmosaic.com/",
          ...config,
        };
        return Promise.resolve();
      });
  };

  @action
  fetchMovie = () => {
    return this.axios
      .get(`${this.config.routes.movie}/${this.config.movieID}/${this.lang}`)
      .then(({ data }) => {
        this.session.initializeMovie(data);

        this.movie = new Movie(this, data);

        this.observers = this.observers.concat([
          observe(this.movie, "remainingChapters", change => {
            if (change.newValue === 0 && change.oldValue === 1) {
              window.analytics.track("Completed All Nodes");
            }
          }),
          observe(this.movie, "remainingDiscoveries", change => {
            if (change.newValue === 0 && change.oldValue === 1) {
              window.analytics.track("Viewed All Discoveries");
            }
          }),
        ]);
      });
  };

  @action
  fetchProfile = () => {
    const profileURL = this.config.routes.profile;

    return this.axios
      .get(profileURL)
      .then(({ data: profiles }) => {
        if (profiles && profiles.length) {
          // documentation says we only use one profile per user
          return profiles[0];
        }

        return this.axios
          .post(profileURL, {
            displayName: this.username,
            image: "",
          })
          .then(({ data }) => Promise.resolve(data));
      })
      .then(profile => {
        this.profile = profile;
        return this.fetchSession();
      });
  };

  @action
  fetchSession = () => {
    const sessionURL = `${this.config.routes.session}/${this.config.movieID}/${
      this.profile._id
    }`;

    return this.axios.get(sessionURL).then(({ data: session }) => {
      this.session = new Session(session);
      return Promise.resolve();
    });
  };

  // I'm too lazy to make fetchSession reuse existing objects so this just updates the history after you've updated a node.
  @action
  fetchHistory = () => {
    const sessionURL = `${this.config.routes.session}/${this.config.movieID}/${
      this.profile._id
    }`;

    return this.axios.get(sessionURL).then(({ data: session }) => {
      this.session.history.replace(session.history);
      this.session.historyIndex = session.historyIndex;
      return Promise.resolve();
    });
  };

  @action
  logout = () => {
    clearInterval(this.refreshInterval);

    this.observers.forEach(o => o());
    this.observers = [];

    if (this.cognitoUser || this.isFacebook) {
      window.analytics.track("Sign Out");
    }

    if (this.cognitoUser) {
      this.cognitoUser.signOut();
    } else if (this.isFacebook) {
      window.FB.logout();
    }

    this.cognitoUser = null;
    this.accessToken = null;
    this.facebookId = null;
    this.profile = null;
    this.movie = null;
    this.session = null;
    this.disableMapAnimation = false;
  };

  @action
  loginWithFacebook = () => {
    const { FB, analytics } = window;

    analytics.track("Sign Up with Facebook Button Pressed");

    return new Promise((resolve, reject) => {
      FB.login(data => resolve(this.handleFacebookLogin(data)), {
        scope: "public_profile,email",
      });
    })
      .then(() => {
        return this.fetchInitialData();
      })
      .then(() => {
        this.identify();

        if (this.facebookNotAuthorized) {
          analytics.track("Facebook Sign Up");
          this.facebookNotAuthorized = false;
        } else {
          analytics.track("Facebook Sign In");
        }

        return Promise.resolve();
      })
      .catch(() => {});
  };

  @action
  handleFacebookLogin = ({ authResponse, status }) => {
    const { FB } = window;

    return new Promise((resolve, reject) => {
      if (status === "connected") {
        FB.api("/me?fields=name,email", ({ error, email, id }) => {
          if (error) {
            reject(error);
          } else {
            const { accessToken, expiresIn } = authResponse;
            this.facebookId = id;
            this.username = email || id;
            this.accessToken = accessToken;
            this.accessExpiration = Date.now() + expiresIn * 1000;

            // start the periodic refresh check
            this.refreshSession(true);

            resolve();
          }
        });
      } else {
        // The person is not logged into this app or we are unable to tell.
        reject(new Error(authResponse));
      }
    });
  };

  @action
  login = (email, password, isNew = false) => {
    email = email.toLowerCase();

    this.cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool,
    });

    return new Promise((resolve, reject) => {
      const authDetails = new AuthenticationDetails({
        Username: email,
        Password: password,
      });

      this.cognitoUser.authenticateUser(authDetails, {
        onSuccess: session => {
          this.username = this.cognitoUser.getUsername();

          resolve(
            this.initializeCognitoSession(session).then(() => {
              if (!isNew) {
                window.analytics.track("Sign In");
              }
            })
          );
        },

        onFailure: err => {
          reject(err);
        },

        newPasswordRequired: (userAttributes, requiredAttributes) => {
          // User was signed up by an admin and must provide new
          // password and required attributes, if any, to complete
          // authentication.

          // the api doesn't accept this field back
          delete userAttributes.email_verified;

          // reject with error
          const err = new Error("New Password Required");
          err.code = "NewPasswordRequiredException";
          err.user = this.cognitoUser;
          err.userAttributes = userAttributes;

          this.cognitoUser = null;

          reject(err);
        },
      });
    });
  };

  @action
  signup = (email, password) => {
    email = email.toLowerCase();
    return new Promise((resolve, reject) => {
      const attributes = [
        new CognitoUserAttribute({
          Name: "email",
          Value: email,
        }),
      ];

      userPool.signUp(email, password, attributes, null, (err, result) => {
        if (err) {
          reject(err);
        } else {
          resolve(
            this.login(email, password, true).then(() => {
              window.analytics.track("Sign Up");
              return Promise.resolve();
            })
          );
        }
      });
    });
  };

  @action
  changePassword = (oldPassword, newPassword) => {
    return new Promise((resolve, reject) => {
      this.cognitoUser.changePassword(oldPassword, newPassword, function(
        err,
        result
      ) {
        return err ? reject(err) : resolve(result);
      });
    });
  };

  @action
  forgotPassword = (email = this.username) => {
    const cognitoUser =
      this.cognitoUser ||
      new CognitoUser({
        Username: email.toLowerCase(),
        Pool: userPool,
      });

    return new Promise((resolve, reject) =>
      cognitoUser.forgotPassword({
        onSuccess: function(data) {
          resolve();
        },

        onFailure: function(err) {
          reject(err);
        },
      })
    );
  };

  @action
  resetPassword = (email, password, code) => {
    return new Promise((resolve, reject) => {
      var cognitoUser = new CognitoUser({
        Username: email.toLowerCase(),
        Pool: userPool,
      });

      cognitoUser.confirmPassword(code, password, {
        onSuccess: resolve,
        onFailure: reject,
      });
    });
  };

  @action
  clearHistory = () => {
    return this.axios
      .delete(`/session/${this.config.movieID}/${this.profile._id}`)
      .then(() => {
        window.analytics.track("Reset History");
        this.isInitialized = false;
        this.disableMapAnimation = false;
        return this.fetchInitialData();
      })
      .then(() => {
        setTimeout(() => {
          this.isInitialized = true;
        }, 1000);
      });
  };

  makeVideoPath = id => {
    return `https://hls.${
      process.env.NODE_ENV === "development" ? "qa" : ""
    }pro11.lv3.cdn.watchmosaic.com/${id}_PRO11/base_index_w8_access.m3u8`;
  };

  get defaultVolume() {
    if (window.localStorage) {
      const volume = parseFloat(localStorage.getItem("playerVolume"));

      return volume >= 0 && volume <= 1 ? volume : 1;
    } else {
      return 1;
    }
  }

  set defaultVolume(val) {
    if (window.localStorage) {
      localStorage.setItem("playerVolume", Math.min(1, Math.max(0, val)));
    }
  }

  get defaultCCLanguage() {
    if (window.localStorage) {
      const lang = localStorage.getItem("playerCC");

      return lang || null;
    } else {
      return null;
    }
  }

  set defaultCCLanguage(val) {
    if (window.localStorage) {
      localStorage.setItem("playerCC", val === null ? "" : val);
    }
  }
}



// WEBPACK FOOTER //
// ./src/store.js